import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../lib/api";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { SearchableSelect } from "../components/ui/searchable-select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Search,
  TrendingUp,
  TrendingDown,
  Loader2,
  ChevronRight,
  RefreshCw,
  X,
  Paperclip,
  Calendar,
  Building2,
  Tag,
  User,
  FileText,
  ChevronDown,
  Plus,
  Save,
  Download,
  Image,
  Hash,
  CreditCard,
  Globe,
  Clock,
  Lightbulb,
  Check,
  XCircle,
  GripVertical,
  Flag,
  MessageSquare,
  Send,
  Trash2,
  Link2,
  Copy,
  Eye,
  Sparkles,
  ArrowLeftRight,
  ExternalLink,
  Store,
} from "lucide-react";

// Constants for panel width
const PANEL_WIDTH_KEY = "bankadmin_detail_panel_width";
const DEFAULT_PANEL_WIDTH = 50; // percentage
const MIN_PANEL_WIDTH = 30;
const MAX_PANEL_WIDTH = 70;

// Constants for filter persistence
const TRANSACTIONS_FILTERS_KEY = "bankadmin_transactions_filters";

const TransactionsPage = () => {
  const { user, isManager } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totals, setTotals] = useState({ income: 0, expense: 0, net: 0, income_count: 0, expense_count: 0 });

  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);

  // Selected transaction for detail panel
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [deepLinkTxId, setDeepLinkTxId] = useState(searchParams.get("tx") || null);
  const [shouldScrollToSelected, setShouldScrollToSelected] = useState(false);

  // Resizable panel
  const [detailPanelWidth, setDetailPanelWidth] = useState(() => {
    const saved = localStorage.getItem(PANEL_WIDTH_KEY);
    return saved ? parseFloat(saved) : DEFAULT_PANEL_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const containerRef = useRef(null);
  const scrollAreaRef = useRef(null);

  // Filters - Load from localStorage or URL params
  const getInitialFilters = () => {
    // URL params take priority, then localStorage
    const savedFilters = localStorage.getItem(TRANSACTIONS_FILTERS_KEY);
    const parsed = savedFilters ? JSON.parse(savedFilters) : {};
    
    return {
      search: searchParams.get("search") || parsed.search || "",
      quarter: searchParams.get("quarter") || parsed.quarter || "",
      year: searchParams.get("year") || parsed.year || "",
      month: searchParams.get("month") || parsed.month || "",
      bank: searchParams.get("bank") || parsed.bank || "",
      currency: searchParams.get("currency") || parsed.currency || "",
      categoryId: searchParams.get("category") || parsed.categoryId || "",
      statusId: searchParams.get("status") || parsed.statusId || "",
      direction: searchParams.get("direction") || parsed.direction || "",
      uncategorizedOnly: searchParams.get("uncategorized") === "true" || parsed.uncategorizedOnly || false,
      flaggedOnly: searchParams.get("flagged") === "true" || parsed.flaggedOnly || false,
    };
  };

  const initialFilters = getInitialFilters();
  const [search, setSearch] = useState(initialFilters.search);
  const [quarter, setQuarter] = useState(initialFilters.quarter);
  const [year, setYear] = useState(initialFilters.year);
  const [month, setMonth] = useState(initialFilters.month);
  const [bank, setBank] = useState(initialFilters.bank);
  const [currency, setCurrency] = useState(initialFilters.currency);
  const [categoryId, setCategoryId] = useState(initialFilters.categoryId);
  const [statusId, setStatusId] = useState(initialFilters.statusId);
  const [direction, setDirection] = useState(initialFilters.direction);
  const [uncategorizedOnly, setUncategorizedOnly] = useState(initialFilters.uncategorizedOnly);
  const [flaggedOnly, setFlaggedOnly] = useState(initialFilters.flaggedOnly);
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 768); // Hide filters by default on mobile

  // Save filters to localStorage when they change
  useEffect(() => {
    const filters = {
      search,
      quarter,
      year,
      month,
      bank,
      currency,
      categoryId,
      statusId,
      direction,
      uncategorizedOnly,
      flaggedOnly,
    };
    localStorage.setItem(TRANSACTIONS_FILTERS_KEY, JSON.stringify(filters));
  }, [search, quarter, year, month, bank, currency, categoryId, statusId, direction, uncategorizedOnly, flaggedOnly]);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle resize
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = ((containerRect.right - e.clientX) / containerRect.width) * 100;
      const clampedWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, newWidth));
      
      setDetailPanelWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        localStorage.setItem(PANEL_WIDTH_KEY, String(detailPanelWidth));
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, detailPanelWidth]);

  const fetchData = useCallback(async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Validate month is a valid number 1-12
      const validMonth = month && month !== "all" && !isNaN(parseInt(month)) && parseInt(month) >= 1 && parseInt(month) <= 12 ? month : null;
      
      const params = {
        page: pageNum,
        limit: 50,
        ...(search && { search }),
        ...(quarter && quarter !== "all" && { quarter }),
        ...(year && year !== "all" && { year }),
        ...(validMonth && { month: validMonth }),
        ...(bank && bank !== "all" && { bank }),
        ...(currency && currency !== "all" && { currency }),
        ...(categoryId && categoryId !== "all" && { category_id: categoryId }),
        ...(statusId && statusId !== "all" && { status_id: statusId }),
        ...(direction && direction !== "all" && { direction }),
        ...(uncategorizedOnly && { uncategorized: "true" }),
        ...(flaggedOnly && { flagged: "true" }),
      };

      const [transRes, statusRes, catRes, accRes] = await Promise.all([
        apiService.getTransactions(params),
        apiService.getStatuses(),
        apiService.getCategories(),
        apiService.getAccounts(),
      ]);

      const newTransactions = transRes.data.transactions || [];
      const totalCount = transRes.data.total || 0;
      const totalPages = transRes.data.pages || 1;
      
      if (append) {
        setTransactions(prev => [...prev, ...newTransactions]);
      } else {
        setTransactions(newTransactions);
      }
      
      setTotal(totalCount);
      setHasMore(pageNum < totalPages);
      setTotals(transRes.data.totals || { income: 0, expense: 0, net: 0, income_count: 0, expense_count: 0 });
      setStatuses(statusRes.data || []);
      setCategories(catRes.data || []);
      setAccounts(accRes.data || []);

      if (isManager) {
        const usersRes = await apiService.getUsers();
        setUsers(usersRes.data || []);
      }

      // Auto-select first transaction if none selected and no deep link pending
      if (!append && newTransactions.length > 0 && !selectedTransaction && !deepLinkTxId) {
        setSelectedTransaction(newTransactions[0]);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [search, quarter, year, month, bank, currency, categoryId, statusId, direction, uncategorizedOnly, flaggedOnly, isManager, selectedTransaction, deepLinkTxId]);

  // Load more transactions when scrolling near bottom
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchData(nextPage, true);
    }
  }, [loadingMore, hasMore, page, fetchData]);

  // Handle scroll for infinite loading
  useEffect(() => {
    const scrollContainer = scrollAreaRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Load more when ~10 items (600px) from bottom for smoother experience
      if (scrollHeight - scrollTop - clientHeight < 600 && hasMore && !loadingMore && !loading) {
        loadMore();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [loadMore, hasMore, loadingMore, loading]);

  // Handle deep link - fetch transaction from URL param
  useEffect(() => {
    if (deepLinkTxId && !loading) {
      const loadDeepLinkedTransaction = async () => {
        try {
          const res = await apiService.getTransaction(deepLinkTxId);
          setSelectedTransaction(res.data);
          setShouldScrollToSelected(true); // Scroll to deep-linked transaction
          setDeepLinkTxId(null);
          // Remove tx from URL params
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("tx");
          setSearchParams(newParams, { replace: true });
        } catch (error) {
          console.error("Failed to load deep-linked transaction:", error);
          toast.error("Transaction not found");
          setDeepLinkTxId(null);
        }
      };
      loadDeepLinkedTransaction();
    }
  }, [deepLinkTxId, loading, searchParams, setSearchParams]);

  // Update a single transaction in the list without full refresh
  const updateTransactionInList = useCallback((updatedTransaction) => {
    setTransactions(prev => 
      prev.map(tx => tx.id === updatedTransaction.id ? updatedTransaction : tx)
    );
    // Also update selected transaction if it's the same one
    if (selectedTransaction?.id === updatedTransaction.id) {
      setSelectedTransaction(updatedTransaction);
    }
  }, [selectedTransaction]);

  // Scroll to selected transaction in the list
  const scrollToTransaction = useCallback((txId) => {
    if (!txId) return;
    
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      const element = document.querySelector(`[data-testid="transaction-row-${txId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add a brief highlight effect
        element.classList.add('bg-blue-50');
        setTimeout(() => element.classList.remove('bg-blue-50'), 1500);
      }
    }, 100);
  }, []);

  // Scroll to transaction when selected via deep link or linked transaction click
  useEffect(() => {
    if (shouldScrollToSelected && selectedTransaction && transactions.length > 0) {
      // Check if the selected transaction is in the current list
      const isInList = transactions.some(tx => tx.id === selectedTransaction.id);
      if (isInList) {
        scrollToTransaction(selectedTransaction.id);
      }
      setShouldScrollToSelected(false);
    }
  }, [shouldScrollToSelected, selectedTransaction?.id, transactions, scrollToTransaction]);

  useEffect(() => {
    // Reset to page 1 and fetch fresh data when filters change
    setPage(1);
    setTransactions([]);
    setHasMore(true);
    fetchData(1, false);
  }, [search, quarter, year, month, bank, currency, categoryId, statusId, direction, uncategorizedOnly, flaggedOnly]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled by the useEffect above via the search state
  };

  const handleBulkUpdate = async (field, value) => {
    if (selectedIds.length === 0) {
      toast.error("Select transactions first");
      return;
    }
    try {
      const res = await apiService.bulkUpdateTransactions({
        transaction_ids: selectedIds,
        [field]: value,
      });
      // Report what the route says it wrote, not how many rows were ticked. The
      // two disagreed for every "Unassigned" click: the write was dropped and
      // this still claimed a row count.
      const updated = res?.data?.updated ?? 0;
      toast.success(`Updated ${updated} transaction${updated === 1 ? "" : "s"}`);
      setSelectedIds([]);
      // Refresh from page 1
      setPage(1);
      setTransactions([]);
      setHasMore(true);
      fetchData(1, false);
    } catch (error) {
      toast.error("Bulk update failed");
    }
  };

  const handleBulkToggleFlag = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select transactions first");
      return;
    }
    try {
      // Toggle flag for each selected transaction
      for (const id of selectedIds) {
        await apiService.toggleFlag(id);
      }
      toast.success(`Toggled flag on ${selectedIds.length} transactions`);
      setSelectedIds([]);
      // Refresh from page 1
      setPage(1);
      setTransactions([]);
      setHasMore(true);
      fetchData(1, false);
    } catch (error) {
      toast.error("Bulk flag toggle failed");
    }
  };

  const handleConsolidate = async () => {
    if (selectedIds.length !== 2) {
      toast.error("Select exactly 2 transactions to consolidate");
      return;
    }
    try {
      const result = await apiService.consolidateTransactions(
        selectedIds[0], 
        selectedIds[1],
        "Manual consolidation - Internal transfer"
      );
      toast.success("Transactions consolidated successfully");
      setSelectedIds([]);
      // Update the transactions in the list
      if (result.data.transactions) {
        setTransactions(prev => prev.map(tx => {
          const updated = result.data.transactions.find(u => u.id === tx.id);
          return updated || tx;
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to consolidate transactions");
    }
  };

  const handleUnconsolidate = async (txId1, txId2) => {
    try {
      await apiService.unconsolidateTransactions(txId1, txId2);
      toast.success("Transactions unlinked successfully");
      // Refresh to show updated state
      setPage(1);
      setTransactions([]);
      setHasMore(true);
      fetchData(1, false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlink transactions");
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === transactions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(transactions.map((t) => t.id));
    }
  };

  const toggleSelect = (id, checked) => {
    // checked is a boolean from Checkbox onCheckedChange
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleRowClick = (tx) => {
    if (window.innerWidth < 1024) {
      navigate(`/transactions/${tx.id}`);
    } else {
      // Preserve scroll position when selecting a transaction
      const scrollPos = scrollAreaRef.current?.scrollTop;
      setSelectedTransaction(tx);
      
      // Update URL with transaction ID (for sharing/bookmarking)
      const newUrl = new URL(window.location);
      newUrl.searchParams.set('tx', tx.id);
      window.history.replaceState({}, '', newUrl);
      
      // Restore scroll position after React re-renders
      if (scrollPos !== undefined) {
        requestAnimationFrame(() => {
          if (scrollAreaRef.current) {
            scrollAreaRef.current.scrollTop = scrollPos;
          }
        });
      }
    }
  };

  const clearFilters = () => {
    setSearch("");
    setQuarter("");
    setYear("");
    setMonth("");
    setBank("");
    setCurrency("");
    setCategoryId("");
    setStatusId("");
    setDirection("");
    setUncategorizedOnly(false);
    setFlaggedOnly(false);
    setPage(1);
    // Clear from localStorage
    localStorage.removeItem(TRANSACTIONS_FILTERS_KEY);
  };

  const hasActiveFilters = search || (quarter && quarter !== "all") || (year && year !== "all") || (month && month !== "all") || (bank && bank !== "all") || 
    (currency && currency !== "all") || (categoryId && categoryId !== "all") || 
    (statusId && statusId !== "all") || (direction && direction !== "all") ||
    uncategorizedOnly || flaggedOnly;

  const formatAmount = (amount, dir) => {
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(amount));
    return dir === "income" ? `+${formatted}` : `-${formatted}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  // Generate year options for filter
  const currentYear = new Date().getFullYear();
  const yearOptions = [
    { value: "all", label: "All Years" },
    ...Array.from({ length: 5 }, (_, i) => ({
      value: String(currentYear - i),
      label: String(currentYear - i)
    }))
  ];

  // Generate month options for filter
  const monthOptions = [
    { value: "all", label: "All Months" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const quarterOptions = [
    { value: "all", label: "All Time" },
    { value: `Q1-${currentYear}`, label: `Q1 ${currentYear}` },
    { value: `Q2-${currentYear}`, label: `Q2 ${currentYear}` },
    { value: `Q3-${currentYear}`, label: `Q3 ${currentYear}` },
    { value: `Q4-${currentYear}`, label: `Q4 ${currentYear}` },
    { value: `Q1-${currentYear - 1}`, label: `Q1 ${currentYear - 1}` },
    { value: `Q2-${currentYear - 1}`, label: `Q2 ${currentYear - 1}` },
    { value: `Q3-${currentYear - 1}`, label: `Q3 ${currentYear - 1}` },
    { value: `Q4-${currentYear - 1}`, label: `Q4 ${currentYear - 1}` },
  ];

  const bankOptions = [
    { value: "all", label: "All Banks" },
    { value: "Wise", label: "Wise" },
    { value: "ING", label: "ING" },
  ];

  const currencyOptions = [
    { value: "all", label: "All" },
    { value: "EUR", label: "EUR" },
    { value: "USD", label: "USD" },
  ];

  const directionOptions = [
    { value: "all", label: "All Types" },
    { value: "income", label: "Income" },
    { value: "expense", label: "Expense" },
  ];

  const statusOptions = [
    { value: "all", label: "All Statuses" },
    ...(Array.isArray(statuses) ? statuses : []).map((s) => ({ value: s.id, label: s.name, color: s.color })),
  ];

  const categoryOptions = [
    { value: "all", label: "All Categories" },
    ...(Array.isArray(categories) ? categories : []).map((c) => ({ value: c.id, label: c.name })),
  ];

  const getStatusById = (id) => (Array.isArray(statuses) ? statuses : []).find((s) => s.id === id);
  const getCategoryById = (id) => (Array.isArray(categories) ? categories : []).find((c) => c.id === id);
  const getUserById = (id) => (Array.isArray(users) ? users : []).find((u) => u.id === id);

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="transactions-page">
      {/* Compact Header */}
      <div className="flex-shrink-0 px-3 sm:px-4 py-2 border-b bg-white">
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 min-w-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search..."
                className="pl-8 h-8 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
              />
            </div>
          </form>

          {/* Filter Toggle */}
          <Button
            variant={hasActiveFilters ? "default" : "outline"}
            size="sm"
            className="h-8 flex-shrink-0"
            onClick={() => setShowFilters(!showFilters)}
          >
            <ChevronDown className={`w-4 h-4 sm:mr-1 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            <span className="hidden sm:inline">Filters</span>
            {hasActiveFilters && (
              <Badge className="ml-1 sm:ml-1.5 h-4 px-1 text-xs bg-white/20">
                {[quarter, month, year, bank, currency, categoryId, statusId, direction].filter(v => v && v !== "all").length + (uncategorizedOnly ? 1 : 0) + (flaggedOnly ? 1 : 0)}
              </Badge>
            )}
          </Button>

          {/* Quick Stats */}
          <span className="text-sm text-slate-500 hidden md:inline flex-shrink-0">
            {total.toLocaleString()} transactions
          </span>

          {/* Refresh */}
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {/* Expandable Filters - Mobile Optimized */}
        {showFilters && (
          <div className="mt-2 pt-2 border-t">
            {/* Filter Grid - Responsive */}
            <div className="grid grid-cols-2 sm:grid-cols-4 md:flex md:flex-wrap items-center gap-2">
              <div className="col-span-1">
                <SearchableSelect
                  options={yearOptions}
                  value={year || "all"}
                  onValueChange={(val) => {
                    setYear(val);
                    // Clear quarter when year changes (month works with year)
                    if (val !== "all") setQuarter("");
                  }}
                  placeholder="Year"
                  searchPlaceholder="Search year..."
                  triggerClassName="h-8 sm:h-7 text-xs w-full"
                />
              </div>

              <div className="col-span-1">
                <SearchableSelect
                  options={monthOptions}
                  value={month || "all"}
                  onValueChange={(val) => {
                    setMonth(val);
                    // Clear quarter when month is selected (they're mutually exclusive)
                    if (val !== "all") setQuarter("");
                  }}
                  placeholder="Month"
                  searchPlaceholder="Search month..."
                  triggerClassName="h-8 sm:h-7 text-xs w-full"
                />
              </div>

              <div className="col-span-1">
                <SearchableSelect
                  options={quarterOptions}
                  value={quarter || "all"}
                  onValueChange={(val) => {
                    setQuarter(val);
                    // Clear month when quarter is selected (they're mutually exclusive)
                    if (val !== "all") setMonth("");
                  }}
                  placeholder="Quarter"
                  searchPlaceholder="Search quarter..."
                  triggerClassName="h-8 sm:h-7 text-xs w-full"
                />
              </div>

              <div className="col-span-1">
                <SearchableSelect
                  options={bankOptions}
                  value={bank || "all"}
                  onValueChange={setBank}
                  placeholder="Bank"
                  searchPlaceholder="Search bank..."
                  triggerClassName="h-8 sm:h-7 text-xs w-full"
                />
              </div>

              <div className="col-span-1">
                <SearchableSelect
                  options={currencyOptions}
                  value={currency || "all"}
                  onValueChange={setCurrency}
                  placeholder="Currency"
                  searchPlaceholder="Search..."
                  triggerClassName="h-8 sm:h-7 text-xs w-full"
                />
              </div>

              <div className="col-span-1">
                <SearchableSelect
                  options={statusOptions}
                  value={statusId || "all"}
                  onValueChange={setStatusId}
                  placeholder="Status"
                  searchPlaceholder="Search status..."
                  triggerClassName="h-8 sm:h-7 text-xs w-full"
                />
              </div>

              <div className="col-span-1">
                <SearchableSelect
                  options={categoryOptions}
                  value={categoryId || "all"}
                  onValueChange={setCategoryId}
                  placeholder="Category"
                  searchPlaceholder="Search category..."
                  triggerClassName="h-8 sm:h-7 text-xs w-full"
                />
              </div>

              <div className="col-span-1">
                <SearchableSelect
                  options={directionOptions}
                  value={direction || "all"}
                  onValueChange={setDirection}
                  placeholder="Type"
                  searchPlaceholder="Search..."
                  triggerClassName="h-8 sm:h-7 text-xs w-full"
                />
              </div>
            </div>

            {/* Toggle Filters - Separate Row on Mobile */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 pt-2 border-t sm:border-0 sm:pt-0 sm:mt-2">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={uncategorizedOnly}
                  onChange={(e) => setUncategorizedOnly(e.target.checked)}
                  className="w-4 h-4 sm:w-3.5 sm:h-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-slate-600">Uncategorized</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={flaggedOnly}
                  onChange={(e) => setFlaggedOnly(e.target.checked)}
                  className="w-4 h-4 sm:w-3.5 sm:h-3.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                />
                <Flag className="w-3 h-3 text-orange-500" />
                <span className="text-slate-600">Flagged</span>
              </label>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" className="h-7 text-xs ml-auto" onClick={clearFilters}>
                  <X className="w-3 h-3 mr-1" /> Clear all
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Bulk Actions Bar - Mobile Responsive */}
        {isManager && selectedIds.length > 0 && (
          <div className="mt-2 pt-2 border-t flex flex-wrap items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-700">{selectedIds.length} selected</Badge>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs">Set Category</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="max-h-60 overflow-y-auto">
                {(Array.isArray(categories) ? categories : []).map((c) => (
                  <DropdownMenuItem key={c.id} onClick={() => handleBulkUpdate("category_id", c.id)}>
                    {c.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs">Set Status</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {(Array.isArray(statuses) ? statuses : []).map((s) => (
                  <DropdownMenuItem key={s.id} onClick={() => handleBulkUpdate("status_id", s.id)}>
                    <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: s.color }} />
                    {s.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs">Set Assignee</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="max-h-60 overflow-y-auto">
                <DropdownMenuItem onClick={() => handleBulkUpdate("assigned_user_id", null)}>
                  <User className="w-3 h-3 mr-2 text-slate-400" />
                  Unassigned
                </DropdownMenuItem>
                {(Array.isArray(users) ? users : []).filter(u => u.active !== false).map((u) => (
                  <DropdownMenuItem key={u.id} onClick={() => handleBulkUpdate("assigned_user_id", u.id)}>
                    <User className="w-3 h-3 mr-2 text-slate-400" />
                    {u.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button 
              variant="outline" 
              size="sm" 
              className="h-7 text-xs"
              onClick={handleBulkToggleFlag}
            >
              <Flag className="w-3 h-3 mr-1" />
              Toggle Flag
            </Button>

            {/* Consolidate button - only shown when exactly 2 items selected */}
            {selectedIds.length === 2 && (
              <Button 
                variant="outline" 
                size="sm" 
                className="h-7 text-xs border-indigo-400 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 font-medium"
                onClick={handleConsolidate}
              >
                <Link2 className="w-3 h-3 mr-1" />
                Consolidate
              </Button>
            )}

            <Button variant="ghost" size="sm" className="h-7 text-xs ml-auto" onClick={() => setSelectedIds([])}>
              <X className="w-3 h-3 mr-1" />
              Clear ({selectedIds.length})
            </Button>
          </div>
        )}
      </div>

      {/* Main Content - 2 Column Layout with Resizable Panels */}
      <div ref={containerRef} className="flex-1 flex overflow-hidden">
        {/* Left Panel - Transaction List (Full width on mobile) */}
        <div 
          className="flex flex-col bg-white lg:border-r"
          style={{ width: isMobile ? '100%' : `${100 - detailPanelWidth}%` }}
        >
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
              <Search className="w-10 h-10 text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No transactions found</p>
            </div>
          ) : (
            <>
              {/* List Header */}
              <div className="flex-shrink-0 flex items-center gap-2 sm:gap-3 px-3 py-2 sm:py-1.5 border-b bg-slate-50 text-xs text-slate-500">
                {isManager && (
                  <Checkbox
                    checked={selectedIds.length === transactions.length}
                    onCheckedChange={toggleSelectAll}
                    className="h-4 w-4 sm:h-3.5 sm:w-3.5"
                    data-testid="select-all"
                  />
                )}
                <span className="flex-1">Transaction</span>
                <span className="min-w-[70px] sm:w-20 text-right">Amount</span>
                {isMobile && <span className="w-4"></span>}
              </div>

              {/* Totals Summary Bar - Top */}
              {total > 0 && (
                <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b bg-gradient-to-r from-slate-50 to-white">
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-slate-500 font-medium">
                      {total.toLocaleString()} transactions
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-emerald-500" />
                        <span className="text-emerald-600 font-semibold">
                          €{totals.income?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="text-slate-400">({totals.income_count})</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingDown className="w-3 h-3 text-red-500" />
                        <span className="text-red-500 font-semibold">
                          €{totals.expense?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="text-slate-400">({totals.expense_count})</span>
                      </span>
                      <span className={`font-bold ${totals.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        = €{totals.net?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  {hasActiveFilters && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Filtered</span>
                  )}
                </div>
              )}

              {/* Scrollable List */}
              <ScrollArea className="flex-1" viewportRef={scrollAreaRef}>
                <TooltipProvider>
                {(Array.isArray(transactions) ? transactions : []).map((tx) => {
                  const status = getStatusById(tx.status_id);
                  const category = getCategoryById(tx.category_id);
                  const assignee = getUserById(tx.assigned_user_id);
                  const suggestedCategory = getCategoryById(tx.suggested_category_id);
                  const hasPendingSuggestion = tx.suggested_category_id && tx.category_suggestion_status === "pending" && !tx.category_id;
                  const isSelected = selectedTransaction?.id === tx.id;
                  
                  return (
                    <div
                      key={tx.id}
                      onClick={() => handleRowClick(tx)}
                      className={`flex items-center gap-2 sm:gap-3 px-3 py-2.5 sm:py-2 border-b cursor-pointer transition-colors active:bg-slate-100 ${
                        isSelected && !isMobile ? "bg-emerald-50 border-l-2 border-l-emerald-500" : 
                        hasPendingSuggestion ? "bg-amber-50/50 hover:bg-amber-50" : "hover:bg-slate-50"
                      }`}
                      data-testid={`transaction-row-${tx.id}`}
                    >
                      {isManager && (
                        <Checkbox
                          checked={selectedIds.includes(tx.id)}
                          onCheckedChange={(e) => toggleSelect(tx.id, e)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 sm:h-3.5 sm:w-3.5"
                        />
                      )}
                      
                      {/* Direction Icon */}
                      <div className={`w-8 h-8 sm:w-6 sm:h-6 rounded-lg sm:rounded flex items-center justify-center flex-shrink-0 ${
                        tx.direction === "income" ? "bg-emerald-100" : "bg-red-100"
                      }`}>
                        {tx.direction === "income" ? (
                          <TrendingUp className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-emerald-600" />
                        ) : (
                          <TrendingDown className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-red-500" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium text-slate-900 truncate max-w-[150px] sm:max-w-none">
                            {tx.counterparty_name || tx.description?.substring(0, 30) || "Transaction"}
                          </span>
                          {/* Reconciled indicator */}
                          {tx.reconciled && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex cursor-help">
                                  <Badge 
                                    variant="outline" 
                                    className="h-4 px-1 sm:px-1.5 text-[10px] bg-blue-100 text-blue-700 border-blue-200"
                                  >
                                    <ArrowLeftRight className="w-2.5 h-2.5" />
                                  </Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Reconciled transaction</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {/* Consolidated/Linked indicator */}
                          {tx.matched_transaction_id && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex cursor-help">
                                  <Badge 
                                    variant="outline" 
                                    className="h-4 px-1 sm:px-1.5 text-[10px] bg-indigo-100 text-indigo-700 border-indigo-200"
                                  >
                                    <Link2 className="w-2.5 h-2.5" />
                                  </Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-slate-800 text-white border-slate-700">
                                <p className="font-medium">Linked Transaction</p>
                                {tx.match_details?.match_reason && (
                                  <p className="text-xs text-slate-300">{tx.match_details.match_reason}</p>
                                )}
                                <p className="text-xs text-slate-400 mt-1">Excluded from burn rate</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {/* Flag indicator */}
                          {tx.is_flagged && (
                            <Badge 
                              variant="outline" 
                              className="h-4 px-1 sm:px-1.5 text-[10px] bg-orange-100 text-orange-700 border-orange-200"
                            >
                              <Flag className="w-2.5 h-2.5" />
                            </Badge>
                          )}
                          {/* Category Badge (if set) - Always show */}
                          {category && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge 
                                  variant="outline" 
                                  className="flex h-5 px-1.5 text-[10px] sm:text-xs bg-violet-50 text-violet-700 border-violet-200 cursor-default max-w-[100px] sm:max-w-[140px]"
                                >
                                  <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">{category.name}</span>
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{category.name}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {/* Category Suggestion Indicator (only if no category set) - Hide on small screens */}
                          {hasPendingSuggestion && suggestedCategory && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge 
                                  variant="outline" 
                                  className="hidden sm:flex h-5 px-1.5 text-[10px] sm:text-xs bg-amber-100 text-amber-700 border-amber-300 cursor-help max-w-[120px]"
                                >
                                  <Lightbulb className="w-3 h-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">{suggestedCategory.name}</span>
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                <p className="font-medium text-amber-700">Category Suggestion</p>
                                <p className="text-sm">{suggestedCategory.name}</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  Based on {tx.category_suggestion_based_on} similar transactions 
                                  ({tx.category_suggestion_confidence}% confidence)
                                </p>
                                <p className="text-xs text-amber-600 mt-1 italic">
                                  Needs validation
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs text-slate-400">
                            {formatDate(tx.transaction_date)} · {tx.bank}
                          </span>
                          {/* Status Badge - Smaller on mobile */}
                          {status && (
                            <Badge 
                              className="h-4 px-1 sm:px-1.5 text-[10px] font-medium"
                              style={{ 
                                backgroundColor: `${status.color}20`,
                                color: status.color,
                                borderColor: status.color
                              }}
                              variant="outline"
                            >
                              {status.name}
                            </Badge>
                          )}
                          {/* Assignee - Hide on very small screens */}
                          {assignee && (
                            <span className="hidden sm:flex items-center gap-1 text-xs text-slate-500">
                              <User className="w-3 h-3" />
                              {assignee.name?.split(' ')[0]}
                            </span>
                          )}
                          {/* Show removed user indicator - Hide on very small screens */}
                          {!assignee && tx.assigned_user_removed && (
                            <span className="hidden sm:flex items-center gap-1 text-xs text-slate-400 italic">
                              <User className="w-3 h-3" />
                              {tx.assigned_user_removed_name?.split(' ')[0] || "Removed"}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Amount - More prominent on mobile */}
                      <div className={`text-sm sm:text-sm font-semibold sm:font-medium text-right min-w-[70px] sm:w-20 flex-shrink-0 ${
                        tx.direction === "income" ? "text-emerald-600" : "text-red-500"
                      }`}>
                        {formatAmount(tx.amount, tx.direction)}
                        <div className="text-[10px] sm:text-xs text-slate-400 font-normal">{tx.currency}</div>
                      </div>

                      {/* Mobile chevron indicator */}
                      {isMobile && (
                        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                      )}
                    </div>
                  );
                })}
                </TooltipProvider>
              </ScrollArea>

              {/* Infinite Scroll Status */}
              <div className="flex-shrink-0 flex items-center justify-between px-3 py-1.5 border-t bg-slate-50 text-xs">
                <span className="text-slate-400">
                  Showing {transactions.length} of {total}
                </span>
                <div className="flex items-center gap-2">
                  {loadingMore && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Loading...</span>
                    </div>
                  )}
                  {!loadingMore && hasMore && (
                    <span className="text-slate-400">Scroll for more</span>
                  )}
                  {!hasMore && transactions.length > 0 && (
                    <span className="text-slate-400">All loaded</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Resize Handle */}
        <div
          className="hidden lg:flex w-1 bg-slate-200 hover:bg-emerald-400 cursor-col-resize items-center justify-center transition-colors group"
          onMouseDown={handleMouseDown}
          style={{ cursor: isResizing ? 'col-resize' : 'col-resize' }}
        >
          <div className="w-4 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="w-3 h-3 text-slate-500" />
          </div>
        </div>

        {/* Right Panel - Transaction Detail */}
        <div 
          className="hidden lg:flex flex-col bg-slate-50"
          style={{ width: `${detailPanelWidth}%` }}
        >
          {selectedTransaction ? (
            <TooltipProvider>
              <TransactionDetail
                transaction={selectedTransaction}
                status={getStatusById(selectedTransaction.status_id)}
                category={getCategoryById(selectedTransaction.category_id)}
                suggestedCategory={getCategoryById(selectedTransaction.suggested_category_id)}
                assignee={getUserById(selectedTransaction.assigned_user_id)}
                statuses={statuses}
                categories={categories}
                users={users}
                onUpdate={updateTransactionInList}
                onSelectLinkedTransaction={(tx) => {
                  // Set the linked transaction as selected
                  setSelectedTransaction(tx);
                  setShouldScrollToSelected(true); // Scroll to linked transaction
                  // Update URL
                  const newUrl = new URL(window.location);
                  newUrl.searchParams.set('tx', tx.id);
                  window.history.replaceState({}, '', newUrl);
                  // Scroll to top of detail panel
                  const detailPanel = document.querySelector('.lg\\:flex.flex-col.bg-slate-50');
                  if (detailPanel) detailPanel.scrollTop = 0;
                  // Add transaction to list if not present (for scrolling to work)
                  setTransactions(prev => {
                    const exists = prev.some(t => t.id === tx.id);
                    if (!exists) {
                      // Add at the beginning of the list
                      return [tx, ...prev];
                    }
                    return prev;
                  });
                }}
              />
            </TooltipProvider>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <p>Select a transaction to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Linked Transaction Card Component - Shows the connected transaction
const LinkedTransactionCard = ({ transactionId, matchDetails, onSelectTransaction }) => {
  const [linkedTx, setLinkedTx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLinkedTransaction = async () => {
      setLoading(true);
      try {
        const res = await apiService.getTransaction(transactionId);
        setLinkedTx(res.data);
      } catch (error) {
        console.error("Failed to fetch linked transaction:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLinkedTransaction();
  }, [transactionId]);

  const handleClick = () => {
    if (linkedTx && onSelectTransaction) {
      onSelectTransaction(linkedTx);
    }
  };

  if (loading) {
    return (
      <div className="bg-indigo-50 rounded-lg border border-indigo-200 p-3">
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
          <span className="text-sm text-indigo-600">Loading linked transaction...</span>
        </div>
      </div>
    );
  }

  if (!linkedTx) return null;

  const formatAmount = (amount, direction) => {
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return direction === "expense" ? `-€${formatted}` : `€${formatted}`;
  };

  return (
    <div 
      className="bg-indigo-50 rounded-lg border border-indigo-200 p-3 space-y-2 cursor-pointer hover:bg-indigo-100 transition-colors"
      onClick={handleClick}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-indigo-600 uppercase tracking-wider flex items-center gap-1.5">
          <Link2 className="w-3.5 h-3.5" />
          Linked Transaction
        </h3>
        <Badge variant="outline" className="text-[10px] bg-indigo-100 text-indigo-700 border-indigo-300">
          {matchDetails?.match_type === "manual" ? "Manual Link" : "Auto-matched"}
        </Badge>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            linkedTx.direction === "income" ? "bg-emerald-100" : "bg-red-100"
          }`}>
            {linkedTx.direction === "income" ? (
              <TrendingUp className="w-4 h-4 text-emerald-600" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">
              {linkedTx.counterparty_name || linkedTx.description?.substring(0, 30) || "Transaction"}
            </p>
            <p className="text-xs text-slate-500">
              {new Date(linkedTx.transaction_date).toLocaleDateString("en-US", { 
                month: "short", day: "numeric", year: "numeric" 
              })} · {linkedTx.bank}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className={`text-sm font-semibold ${linkedTx.direction === "income" ? "text-emerald-600" : "text-red-500"}`}>
            {formatAmount(linkedTx.amount, linkedTx.direction)}
          </p>
          <p className="text-xs text-slate-400">{linkedTx.currency}</p>
        </div>
      </div>
      
      {matchDetails?.match_reason && (
        <p className="text-xs text-indigo-600 pt-1 border-t border-indigo-200">
          {matchDetails.match_reason}
        </p>
      )}
      
      <div className="flex items-center gap-1 text-xs text-indigo-500 pt-1">
        <ExternalLink className="w-3 h-3" />
        Click to view linked transaction
      </div>
    </div>
  );
};

// Transaction Detail Component - Full info displayed without clicking
// What a vendor sends, in the words somebody chasing the document needs.
const DOCUMENT_KIND_LABEL = {
  invoice: "Invoice",
  invoice_plus_receipt: "Invoice and a separate receipt",
  monthly_statement: "One monthly invoice covering several charges",
  none: "No document exists",
};

const CADENCE_LABEL = {
  per_transaction: "one per charge",
  monthly: "monthly",
  annual: "annual",
};

/**
 * Where this charge's document comes from.
 *
 * Sits on the transaction rather than on a vendor page because that is where
 * the question gets asked. The portal is the primary action: everything else
 * here exists to get somebody to the right page of the right account.
 *
 * No profile is an ordinary state, not an error, so it reads as an invitation
 * to record one rather than as something broken.
 */
const VendorProfileBlock = ({ transaction }) => {
  const navigate = useNavigate();
  const profile = transaction.vendor_profile;

  if (!profile) {
    return (
      <div className="border border-dashed border-slate-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Store className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-slate-700">No vendor profile</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Nobody has recorded where this supplier's invoices come from yet.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs mt-3"
              onClick={() =>
                navigate("/vendors", { state: { fromTransaction: transaction } })
              }
            >
              <Plus className="w-3 h-3 mr-1.5" />
              Record it
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const noDocument = profile.document_kind === "none";

  return (
    <div
      className={`border rounded-lg p-4 ${
        noDocument ? "border-slate-200 bg-slate-50" : "border-emerald-200 bg-emerald-50/50"
      }`}
    >
      <div className="flex items-start gap-3">
        <Store
          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
            noDocument ? "text-slate-400" : "text-emerald-600"
          }`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-medium text-slate-800 truncate">{profile.name}</h4>
            <button
              type="button"
              onClick={() => navigate("/vendors", { state: { openProfileId: profile.id } })}
              className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 whitespace-nowrap"
            >
              Edit
            </button>
          </div>

          <p className="text-xs text-slate-600 mt-1">
            {DOCUMENT_KIND_LABEL[profile.document_kind] || profile.document_kind}
            {!noDocument && profile.cadence
              ? `, ${CADENCE_LABEL[profile.cadence] || profile.cadence}`
              : ""}
          </p>

          {noDocument && profile.no_document_reason && (
            <p className="text-xs text-slate-500 mt-1">{profile.no_document_reason}</p>
          )}

          {profile.account_ref && (
            <p className="text-xs text-slate-500 mt-1 font-mono">{profile.account_ref}</p>
          )}

          {profile.portal_hint && (
            <p className="text-xs text-slate-500 mt-1">{profile.portal_hint}</p>
          )}

          {!noDocument && (
            profile.portal_url ? (
              <a
                href={profile.portal_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-emerald-700 hover:text-emerald-900"
              >
                Fetch the invoice
                <ExternalLink className="w-3 h-3" />
              </a>
            ) : (
              <p className="text-xs text-slate-500 mt-3">
                No portal recorded yet.{" "}
                <button
                  type="button"
                  onClick={() => navigate("/vendors", { state: { openProfileId: profile.id } })}
                  className="underline underline-offset-2 hover:text-slate-800"
                >
                  Add one
                </button>
              </p>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const TransactionDetail = ({ transaction, status, category, suggestedCategory, assignee, statuses, categories, users, onUpdate, onSelectLinkedTransaction }) => {
  const { user: currentUser } = useAuth();
  const [updating, setUpdating] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [acceptingSuggestion, setAcceptingSuggestion] = useState(false);
  const [togglingFlag, setTogglingFlag] = useState(false);
  const fileInputRef = useRef(null);
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Attachment preview modal state
  const [previewingAttachment, setPreviewingAttachment] = useState(null);
  
  // Suggested attachments state
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [attachingSuggestion, setAttachingSuggestion] = useState(null);
  
  // File search state
  const [fileSearch, setFileSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchingFiles, setSearchingFiles] = useState(false);
  const [showFileSearch, setShowFileSearch] = useState(false);

  // Check if there's a pending suggestion
  const hasPendingSuggestion = transaction.suggested_category_id &&
    transaction.category_suggestion_status === "pending" && 
    !transaction.category_id;

  // Status and category options for searchable selects
  const statusOptions = (Array.isArray(statuses) ? statuses : []).map((s) => ({ value: s.id, label: s.name, color: s.color }));
  const categoryOptions = (Array.isArray(categories) ? categories : []).map((c) => ({ value: c.id, label: c.name }));
  // Only show active users in the dropdown
  const activeUsers = (Array.isArray(users) ? users : []).filter(u => u.active !== false && u.deleted !== true);
  const userOptions = [
    { value: "", label: "Unassigned" },
    ...activeUsers.map((u) => ({ value: u.id, label: u.name })),
  ];

  // Fetch attachments, messages and suggestions when transaction changes
  useEffect(() => {
    const fetchData = async () => {
      setLoadingAttachments(true);
      setLoadingMessages(true);
      setLoadingSuggestions(true);
      try {
        const [txRes, msgRes, suggestRes] = await Promise.all([
          apiService.getTransaction(transaction.id),
          apiService.getMessages(transaction.id),
          apiService.getTransactionSuggestions(transaction.id).catch(() => ({ data: { suggestions: [] } }))
        ]);
        setAttachments(txRes.data.attachments || []);
        setMessages(msgRes.data || []);
        
        // Filter out already-attached files from suggestions
        const attachedIds = new Set((txRes.data.attachments || []).map(a => a.id));
        const filteredSuggestions = (suggestRes.data.suggestions || []).filter(
          s => !attachedIds.has(s.file_id)
        );
        setSuggestions(filteredSuggestions);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoadingAttachments(false);
        setLoadingMessages(false);
        setLoadingSuggestions(false);
      }
    };
    fetchData();
  }, [transaction.id]);

  const writeField = async (field, value, message) => {
    setUpdating(true);
    try {
      const res = await apiService.updateTransaction(transaction.id, { [field]: value });
      toast.success(message);
      onUpdate(res.data); // Pass updated transaction to parent
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateField = async (field, value) => {
    if (!value || value === "") return; // Don't update with empty
    await writeField(field, value, "Updated");
  };

  // The assignee is the one field here with a real empty state, so it cannot go
  // through the guard above: the select sends "" for its Unassigned option, and
  // the API needs an explicit null to tell a clear from an omission.
  const handleAssigneeChange = async (userId) =>
    writeField("assigned_user_id", userId || null, userId ? "Updated" : "Unassigned");

  const handleToggleFlag = async () => {
    setTogglingFlag(true);
    try {
      const res = await apiService.toggleFlag(transaction.id);
      toast.success(res.data.is_flagged ? "Transaction flagged" : "Flag removed");
      onUpdate(res.data);
    } catch (error) {
      toast.error("Failed to toggle flag");
    } finally {
      setTogglingFlag(false);
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/transactions?tx=${transaction.id}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success("Link copied to clipboard");
    }).catch(() => {
      toast.error("Failed to copy link");
    });
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const res = await apiService.addMessage(transaction.id, newMessage.trim());
      setMessages(prev => [...prev, res.data]);
      setNewMessage("");
      toast.success("Message added");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await apiService.deleteMessage(transaction.id, messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success("Message deleted");
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadRes = await apiService.uploadFile(file, transaction.counterparty_name || "receipt");
      const fileId = uploadRes.data.id;
      await apiService.attachFile(transaction.id, fileId);
      toast.success("File uploaded and attached");
      const res = await apiService.getTransaction(transaction.id);
      setAttachments(res.data.attachments || []);
      // Don't trigger full refresh for file uploads, just update attachments
    } catch (error) {
      const message = error.response?.data?.detail || "Upload failed";
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAttachSuggestion = async (suggestion) => {
    setAttachingSuggestion(suggestion.file_id);
    try {
      await apiService.attachFile(transaction.id, suggestion.file_id);
      toast.success(`Attached "${suggestion.filename}"`);
      
      // Refresh the full transaction to get updated status
      const res = await apiService.getTransaction(transaction.id);
      const updatedTransaction = res.data;
      setAttachments(updatedTransaction.attachments || []);
      
      // Update the transaction in the parent list (triggers status badge update)
      onUpdate(updatedTransaction);
      
      // Remove from suggestions
      setSuggestions(prev => prev.filter(s => s.file_id !== suggestion.file_id));
      
      // Remove from search results if present
      setSearchResults(prev => prev.filter(f => f.id !== suggestion.file_id));
    } catch (error) {
      toast.error("Failed to attach file");
    } finally {
      setAttachingSuggestion(null);
    }
  };

  const handleFileSearch = async (query) => {
    setFileSearch(query);
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchingFiles(true);
    try {
      const res = await apiService.getFiles();
      const allFiles = res.data || [];
      
      // Get already attached file IDs
      const attachedIds = new Set(attachments.map(a => a.id));
      
      // Filter files by search query and exclude already attached
      const filtered = allFiles.filter(file => {
        if (attachedIds.has(file.id)) return false;
        const searchLower = query.toLowerCase();
        const filename = (file.filename || '').toLowerCase();
        const merchant = (file.analysis?.document_info?.merchant_name || '').toLowerCase();
        return filename.includes(searchLower) || merchant.includes(searchLower);
      });
      
      setSearchResults(filtered.slice(0, 5));
    } catch (error) {
      console.error("Failed to search files:", error);
    } finally {
      setSearchingFiles(false);
    }
  };

  const handleAttachSearchResult = async (file) => {
    setAttachingSuggestion(file.id);
    try {
      await apiService.attachFile(transaction.id, file.id);
      toast.success(`Attached "${file.filename}"`);
      
      // Refresh the full transaction to get updated status
      const res = await apiService.getTransaction(transaction.id);
      const updatedTransaction = res.data;
      setAttachments(updatedTransaction.attachments || []);
      
      // Update the transaction in the parent list (triggers status badge update)
      onUpdate(updatedTransaction);
      
      // Remove from search results
      setSearchResults(prev => prev.filter(f => f.id !== file.id));
      
      // Clear search
      setFileSearch("");
      setShowFileSearch(false);
    } catch (error) {
      toast.error("Failed to attach file");
    } finally {
      setAttachingSuggestion(null);
    }
  };

  const handleAcceptSuggestion = async () => {
    setAcceptingSuggestion(true);
    try {
      const res = await apiService.acceptCategorySuggestion(transaction.id);
      toast.success("Category suggestion accepted");
      onUpdate(res.data);
    } catch (error) {
      toast.error("Failed to accept suggestion");
    } finally {
      setAcceptingSuggestion(false);
    }
  };

  const handleRejectSuggestion = async () => {
    setAcceptingSuggestion(true);
    try {
      const res = await apiService.rejectCategorySuggestion(transaction.id);
      toast.info("Category suggestion rejected");
      onUpdate(res.data);
    } catch (error) {
      toast.error("Failed to reject suggestion");
    } finally {
      setAcceptingSuggestion(false);
    }
  };

  const formatFullDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric", year: "numeric"
    });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes("image")) return <Image className="w-4 h-4 text-blue-500" />;
    if (contentType?.includes("pdf")) return <FileText className="w-4 h-4 text-red-500" />;
    return <Paperclip className="w-4 h-4 text-slate-500" />;
  };

  return (
    <ScrollArea className="flex-1">
      <div className="p-4 sm:p-6 space-y-4">
        {/* Header - Improved Layout */}
        <div className="space-y-3">
          {/* Top row: Icon, Name, Actions */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                transaction.direction === "income" ? "bg-emerald-100" : "bg-red-100"
              }`}>
                {transaction.direction === "income" ? (
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                ) : (
                  <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                )}
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
                  {transaction.counterparty_name || "Transaction"}
                </h2>
                <p className="text-sm text-slate-500">{transaction.bank} · {transaction.currency}</p>
              </div>
            </div>
            {/* Action buttons */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={handleCopyLink}
                    data-testid="copy-link-btn"
                  >
                    <Link2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Copy link to transaction</p>
                </TooltipContent>
              </Tooltip>
              <Button
                variant={transaction.is_flagged ? "default" : "outline"}
                size="sm"
                className={`h-8 w-8 p-0 ${transaction.is_flagged ? "bg-orange-500 hover:bg-orange-600" : ""}`}
                onClick={handleToggleFlag}
                disabled={togglingFlag}
              >
                {togglingFlag ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Flag className={`w-4 h-4 ${transaction.is_flagged ? "fill-white" : ""}`} />
                )}
              </Button>
            </div>
          </div>
          
          {/* Second row: Amount - Prominent display */}
          <div className={`flex items-center justify-between py-2 px-3 rounded-lg ${
            transaction.direction === "income" ? "bg-emerald-50" : "bg-red-50"
          }`}>
            <span className="text-sm text-slate-600">Amount</span>
            <div className={`text-xl sm:text-2xl font-bold ${
              transaction.direction === "income" ? "text-emerald-600" : "text-red-600"
            }`}>
              {transaction.direction === "income" ? "+" : "-"}
              {Math.abs(transaction.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}
              <span className="text-sm font-normal text-slate-500 ml-1">{transaction.currency}</span>
            </div>
          </div>
          
          {/* Reconciled indicator */}
          {transaction.reconciled && (
            <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <ArrowLeftRight className="w-4 h-4 text-blue-500" />
              <span className="text-sm text-blue-700 font-medium">This transaction has been reconciled</span>
            </div>
          )}
          
          {/* Flagged indicator if flagged */}
          {transaction.is_flagged && (
            <div className="flex items-center gap-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
              <Flag className="w-4 h-4 text-orange-500 fill-orange-200" />
              <span className="text-sm text-orange-700 font-medium">This transaction is flagged for review</span>
            </div>
          )}
        </div>

        {/* Quick Actions - Searchable Selects */}
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Status</label>
            <SearchableSelect
              options={statusOptions}
              value={transaction.status_id || ""}
              onValueChange={(v) => handleUpdateField("status_id", v)}
              placeholder="Set status"
              searchPlaceholder="Search status..."
              disabled={updating}
              triggerClassName="h-9"
              renderValue={(opt) => opt ? (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: opt.color }} />
                  {opt.label}
                </span>
              ) : null}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Category</label>
            <SearchableSelect
              options={categoryOptions}
              value={transaction.category_id || ""}
              onValueChange={(v) => handleUpdateField("category_id", v)}
              placeholder="Set category"
              searchPlaceholder="Search category..."
              disabled={updating}
              triggerClassName="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Assignee</label>
            <SearchableSelect
              options={userOptions}
              value={transaction.assigned_user_id || ""}
              onValueChange={handleAssigneeChange}
              placeholder="Assign to..."
              searchPlaceholder="Search users..."
              disabled={updating}
              triggerClassName="h-9"
            />
          </div>
        </div>

        <VendorProfileBlock transaction={transaction} />

        {/* Category Suggestion Alert */}
        {hasPendingSuggestion && suggestedCategory && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-4 h-4 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-amber-800 text-sm">Category Suggestion</h4>
                <p className="text-amber-700 text-sm mt-0.5">
                  Based on <strong>{transaction.category_suggestion_based_on}</strong> similar transactions,
                  we suggest: <strong>{suggestedCategory.name}</strong>
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  {transaction.category_suggestion_confidence}% confidence · Needs validation
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    className="h-7 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={handleAcceptSuggestion}
                    disabled={acceptingSuggestion}
                  >
                    {acceptingSuggestion ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Check className="w-3 h-3 mr-1" />
                    )}
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={handleRejectSuggestion}
                    disabled={acceptingSuggestion}
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full Transaction Details - Always Visible */}
        <div className="bg-white rounded-lg border p-4 space-y-3">
          <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider">Transaction Details</h3>
          
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-500">
              <Calendar className="w-4 h-4" />
              Date
            </div>
            <div className="text-slate-900 font-medium">{formatFullDate(transaction.transaction_date)}</div>
            
            <div className="flex items-center gap-2 text-slate-500">
              <Building2 className="w-4 h-4" />
              Bank
            </div>
            <div className="text-slate-900">{transaction.bank}</div>
            
            <div className="flex items-center gap-2 text-slate-500">
              <Globe className="w-4 h-4" />
              Currency
            </div>
            <div className="text-slate-900">{transaction.currency}</div>
            
            <div className="flex items-center gap-2 text-slate-500">
              <CreditCard className="w-4 h-4" />
              Account
            </div>
            <div className="text-slate-900 font-mono text-xs">{transaction.account_id || "—"}</div>
            
            {transaction.reference && (
              <>
                <div className="flex items-center gap-2 text-slate-500">
                  <Hash className="w-4 h-4" />
                  Reference
                </div>
                <div className="text-slate-900 font-mono text-xs truncate">{transaction.reference}</div>
              </>
            )}

            {transaction.counterparty_iban && (
              <>
                <div className="flex items-center gap-2 text-slate-500">
                  <CreditCard className="w-4 h-4" />
                  IBAN
                </div>
                <div className="text-slate-900 font-mono text-xs truncate">{transaction.counterparty_iban}</div>
              </>
            )}

            {transaction.fee_amount > 0 && (
              <>
                <div className="flex items-center gap-2 text-slate-500">
                  <TrendingDown className="w-4 h-4" />
                  Fee
                </div>
                <div className="text-red-500">{transaction.fee_amount.toFixed(2)} {transaction.currency}</div>
              </>
            )}

            {transaction.fx_rate && (
              <>
                <div className="flex items-center gap-2 text-slate-500">
                  <Globe className="w-4 h-4" />
                  FX Rate
                </div>
                <div className="text-slate-900">{transaction.fx_rate}</div>
              </>
            )}

            {transaction.cardholder_name && (
              <>
                <div className="flex items-center gap-2 text-slate-500">
                  <User className="w-4 h-4" />
                  Cardholder
                </div>
                <div className="text-slate-900">{transaction.cardholder_name}</div>
              </>
            )}

            <div className="flex items-center gap-2 text-slate-500">
              <Clock className="w-4 h-4" />
              Created
            </div>
            <div className="text-slate-900 text-xs">
              {new Date(transaction.created_at).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Description */}
        {transaction.description && (
          <div className="bg-white rounded-lg border p-3 space-y-1">
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider">Description</h3>
            <p className="text-sm text-slate-600">{transaction.description}</p>
          </div>
        )}

        {/* Linked Transaction */}
        {transaction.matched_transaction_id && (
          <LinkedTransactionCard 
            transactionId={transaction.matched_transaction_id}
            matchDetails={transaction.match_details}
            onSelectTransaction={onSelectLinkedTransaction}
          />
        )}

        {/* Remittance Info */}
        {transaction.remittance_info && (
          <div className="bg-white rounded-lg border p-3 space-y-1">
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider">Remittance Info</h3>
            <p className="text-sm text-slate-600">{transaction.remittance_info}</p>
          </div>
        )}

        {/* Attachments */}
        <div className="bg-white rounded-lg border p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5" />
              Attachments ({attachments.length})
            </h3>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Plus className="w-3 h-3 mr-1" />
              )}
              Upload
            </Button>
          </div>
          
          {loadingAttachments ? (
            <div className="py-4 text-center">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
            </div>
          ) : attachments.length > 0 ? (
            <div className="space-y-2">
              {attachments.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => setPreviewingAttachment(file)}
                  data-testid={`attachment-${file.id}`}
                >
                  {getFileIcon(file.content_type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{file.filename}</p>
                    <p className="text-xs text-slate-400">{formatSize(file.size)}</p>
                  </div>
                  <Eye className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-2">No attachments yet</p>
          )}
        </div>

        {/* Suggested Attachments */}
        {loadingSuggestions ? (
          <div className="bg-white rounded-lg border p-3">
            <div className="py-2 text-center">
              <Loader2 className="w-4 h-4 animate-spin text-purple-400 mx-auto" />
              <p className="text-xs text-slate-400 mt-1">Finding matches...</p>
            </div>
          </div>
        ) : (
          <div className="bg-slate-50 rounded-lg border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                {suggestions.length > 0 ? `Suggested (${suggestions.length})` : 'Find Documents'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs text-slate-500 hover:text-slate-700"
                onClick={() => setShowFileSearch(!showFileSearch)}
              >
                <Search className="w-3 h-3 mr-1" />
                Search
              </Button>
            </div>
            
            {/* File Search Box */}
            {showFileSearch && (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    value={fileSearch}
                    onChange={(e) => handleFileSearch(e.target.value)}
                    placeholder="Search inbox..."
                    className="h-8 pl-7 text-sm"
                    data-testid="file-search-input"
                  />
                  {searchingFiles && (
                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-slate-400" />
                  )}
                </div>
                
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {searchResults.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center gap-2 p-2 bg-white rounded-lg border hover:border-emerald-300 transition-colors"
                      >
                        <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-3.5 h-3.5 text-slate-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">{file.filename}</p>
                          {file.analysis?.document_info?.merchant_name && (
                            <p className="text-xs text-slate-400 truncate">{file.analysis.document_info.merchant_name}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleAttachSearchResult(file)}
                          disabled={attachingSuggestion === file.id}
                        >
                          {attachingSuggestion === file.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Plus className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {fileSearch.length >= 2 && searchResults.length === 0 && !searchingFiles && (
                  <p className="text-xs text-slate-400 text-center py-2">No matching files found</p>
                )}
              </div>
            )}
            
            {/* Suggestions */}
            {suggestions.length > 0 ? (
              <div className="space-y-2">
                {suggestions.slice(0, 3).map((suggestion) => (
                  <div
                    key={suggestion.file_id}
                    className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-100"
                  >
                    <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{suggestion.filename}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {suggestion.document_info?.merchant_name && (
                          <span>{suggestion.document_info.merchant_name}</span>
                        )}
                        {suggestion.document_info?.amount && (
                          <span className="font-medium">€{suggestion.document_info.amount}</span>
                        )}
                        {suggestion.match_score && (
                          <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 py-0">
                            {suggestion.match_score}% match
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-purple-600 hover:text-purple-700 hover:bg-purple-100"
                      onClick={() => handleAttachSuggestion(suggestion)}
                      disabled={attachingSuggestion === suggestion.file_id}
                      data-testid={`attach-suggestion-${suggestion.file_id}`}
                    >
                      {attachingSuggestion === suggestion.file_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            ) : !showFileSearch && (
              <p className="text-xs text-slate-400 text-center py-2">
                No matching documents found. Use search to find files manually.
              </p>
            )}
          </div>
        )}

        {/* Messages (Comments History) */}
        <div className="bg-white rounded-lg border p-3 space-y-3">
          <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" />
            Comments ({messages.length})
          </h3>
          
          {/* Messages List */}
          {loadingMessages ? (
            <div className="py-4 text-center">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
            </div>
          ) : messages.length > 0 ? (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {messages.map((msg) => (
                <div key={msg.id} className="bg-slate-50 rounded-lg p-2.5 group">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-slate-700">{msg.user_name}</span>
                        <span className="text-slate-400">
                          {new Date(msg.created_at).toLocaleDateString()} {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {(msg.user_id === currentUser?.id || currentUser?.role === "admin") && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500"
                        onClick={() => handleDeleteMessage(msg.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-2">No comments yet</p>
          )}

          {/* Add Message Form */}
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 h-8 text-sm"
            />
            <Button
              type="submit"
              size="sm"
              className="h-8 bg-emerald-600 hover:bg-emerald-700"
              disabled={sendingMessage || !newMessage.trim()}
            >
              {sendingMessage ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
        </div>

        {/* Open Full View Link */}
        <Button variant="outline" size="sm" className="w-full" asChild>
          <a href={`/transactions/${transaction.id}`}>
            Open Full View
          </a>
        </Button>
      </div>

      {/* Attachment Preview Modal */}
      <Dialog open={!!previewingAttachment} onOpenChange={() => setPreviewingAttachment(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${
                previewingAttachment?.content_type?.startsWith("image/") ? "bg-purple-100" : "bg-blue-100"
              } flex items-center justify-center`}>
                {previewingAttachment?.content_type?.startsWith("image/") ? (
                  <Image className="w-5 h-5 text-purple-600" />
                ) : (
                  <FileText className="w-5 h-5 text-blue-600" />
                )}
              </div>
              <div>
                <p className="font-medium">{previewingAttachment?.filename}</p>
                <p className="text-sm text-slate-500 font-normal">
                  {previewingAttachment && formatSize(previewingAttachment.size)}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {previewingAttachment?.content_type?.startsWith("image/") && previewingAttachment?.data ? (
              <div className="rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
                <img
                  src={`data:${previewingAttachment.content_type};base64,${previewingAttachment.data}`}
                  alt={previewingAttachment.filename}
                  className="max-h-[60vh] object-contain"
                />
              </div>
            ) : previewingAttachment?.content_type === "application/pdf" && previewingAttachment?.data ? (
              <div className="rounded-lg overflow-hidden bg-slate-100">
                <iframe
                  src={`data:application/pdf;base64,${previewingAttachment.data}`}
                  title={previewingAttachment.filename}
                  className="w-full h-[60vh]"
                />
              </div>
            ) : (
              <div className="rounded-lg bg-slate-100 p-8 text-center">
                <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">Preview not available</p>
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-center gap-3">
            {previewingAttachment?.data && (
              <a
                href={`data:${previewingAttachment.content_type};base64,${previewingAttachment.data}`}
                download={previewingAttachment.filename}
              >
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </a>
            )}
            <Button variant="outline" onClick={() => setPreviewingAttachment(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </ScrollArea>
  );
};

export default TransactionsPage;
